// Caches the original File (.eml/.msg) the user opened, keyed by the locally-parsed
// item id. Lets "Forward as attachment" attach the real file bytes, since these
// client-parsed items have a synthetic id with no server item to reference.
//
// Memory is bounded two ways so repeated opens of large files can't grow the cache
// unbounded: a max entry count and a max total byte budget. Entries are evicted
// oldest-first (insertion-order LRU) until both limits are satisfied.
const MAX_CACHED_FILES = 20;
const MAX_CACHED_BYTES = 100 * 1024 * 1024; // 100 MB total across all cached files

const localFileMessageStore = new Map<string, File>();
let cachedBytes = 0;

function deleteEntry(itemId: string): void {
    const existing = localFileMessageStore.get(itemId);
    if (existing) {
        cachedBytes -= existing.size;
        localFileMessageStore.delete(itemId);
    }
}

export function setLocalFileMessage(itemId: string, file: File): void {
    if (!itemId) {
        return;
    }
    
    // A single file larger than the whole budget can never be cached without
    // immediately blowing it, so skip it rather than evicting everything else.
    if (file.size > MAX_CACHED_BYTES) {
        deleteEntry(itemId);
        return;
    }
    
    // Refresh insertion order so re-opened files are treated as most recent.
    deleteEntry(itemId);
    localFileMessageStore.set(itemId, file);
    cachedBytes += file.size;
    
    // Evict oldest entries until both the count and total-byte budgets are met.
    while (localFileMessageStore.size > MAX_CACHED_FILES || cachedBytes > MAX_CACHED_BYTES) {
        const oldestKey = localFileMessageStore.keys().next().value;
        if (oldestKey === undefined || oldestKey === itemId) {
            break;
        }
        deleteEntry(oldestKey);
    }
}

export function getLocalFileMessage(itemId: string | undefined): File | undefined {
    return itemId ? localFileMessageStore.get(itemId) : undefined;
}
